import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { School, Mail, Lock, Building2, Eye, EyeOff, ChevronRight, KeyRound, ArrowLeft } from "lucide-react";

export default function SchoolLoginForm({ onBack }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("login"); // login, otp
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolCode, setSchoolCode] = useState("");
  const [otp, setOtp] = useState("");
  
  // OTP info
  const [otpEmail, setOtpEmail] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !schoolCode) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (data.requires_otp) {
        // Move to OTP step
        setOtpEmail(data.email_hint || email);
        setStep("otp");
        toast.success("OTP sent to your school email");
      } else if (response.ok && data.success) {
        toast.success(`Welcome! Redirecting to school dashboard...`);
        navigate("/school/dashboard");
      } else {
        toast.error(data.detail || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Login successful! Redirecting...");
        navigate("/school/dashboard");
      } else {
        toast.error(data.detail || "Invalid OTP");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (data.requires_otp) {
        toast.success("OTP resent successfully");
      } else {
        toast.error(data.detail || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <School className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">School Portal</h2>
        <p className="text-gray-500 mt-1">
          {step === "login" ? "Sign in to your school admin account" : "Enter the OTP sent to your email"}
        </p>
      </div>

      {step === "login" ? (
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="schoolCode">School Code</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="schoolCode"
                type="text"
                placeholder="Enter school code (e.g., EDU001)"
                value={schoolCode}
                onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                className="pl-10 uppercase"
                data-testid="school-code-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">School Admin ID</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="email"
                type="text"
                placeholder="Admin ID (e.g., S_123456)"
                value={email}
                onChange={(e) => setEmail(e.target.value.toUpperCase())}
                className="pl-10"
                data-testid="school-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                data-testid="school-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900"
            disabled={loading}
            data-testid="school-login-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Signing in...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Continue
                <ChevronRight size={16} />
              </span>
            )}
          </Button>

          <p className="text-center text-sm text-gray-500 mt-4">
            New school?{" "}
            <a href="/school/register" className="text-teal-600 hover:text-teal-700 font-medium">
              Register your school
            </a>
          </p>
        </form>
      ) : (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-emerald-700">
              A 6-digit verification code has been sent to <strong>{otpEmail}</strong>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="otp">Verification Code</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="otp"
                type="text"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="pl-10 text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                data-testid="school-otp-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900"
            disabled={loading || otp.length !== 6}
            data-testid="school-verify-otp-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Verifying...
              </span>
            ) : (
              "Verify & Login"
            )}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                setStep("login");
                setOtp("");
              }}
              className="flex items-center gap-1 text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft size={14} />
              Back
            </button>
            <button
              type="button"
              onClick={handleResendOTP}
              className="text-teal-600 hover:text-teal-700"
              disabled={loading}
            >
              Resend OTP
            </button>
          </div>
        </form>
      )}

      <p className="text-center text-xs text-gray-500">
        For B2B school partnerships. Contact us at{" "}
        <a href="mailto:info@contentiumtech.com" className="text-emerald-600 hover:underline">
          info@contentiumtech.com
        </a>
      </p>
    </div>
  );
}
